import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BarChart3, Facebook, Instagram, Building2, Star, Camera, UsersRound, Gift, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Iter50 P1 — Consolidated Marketing Dashboard.
 * Sections (visually + logically separated):
 * 1. Filters (date range, channel, status)
 * 2. Connection health (FB/IG/GBP)
 * 3. Per-channel publishing matrix (awaiting/ready/publishing/published/failed/retry)
 * 4. Neutral Google review requests + review-growth (SEPARATE from promotions/referrals)
 * 5. Photo/social promotion pipeline + earned discounts (SEPARATE)
 * 6. Referrals (SEPARATE)
 * 7. Tremendous (spend, failures, fraud reviews, balance alerts)
 */
export default function MarketingDashboard() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400e3).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [channel, setChannel] = useState("");
  const [status, setStatus] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("date_from", from); params.set("date_to", to);
      if (channel) params.set("channel", channel);
      if (status) params.set("status", status);
      const { data } = await api.get(`/marketing/dashboard?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load dashboard");
    } finally { setLoading(false); }
  }, [from, to, channel, status]);
  useEffect(() => { load(); }, [load]);

  const fmt$ = (c) => `$${((c || 0) / 100).toFixed(2)}`;

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-7xl" data-testid="marketing-dashboard-page">
      <header className="flex items-center gap-3">
        <BarChart3 className="w-6 h-6 text-[#FF5A00]"/>
        <div>
          <h1 className="font-heading text-2xl font-bold">Marketing Dashboard</h1>
          <p className="text-sm text-zinc-500">Per-channel publishing, neutral review requests, photo/social promotions, referrals, and Tremendous spend — kept intentionally separated.</p>
        </div>
      </header>

      <FilterBar
        from={from} to={to} channel={channel} status={status}
        onFrom={setFrom} onTo={setTo} onChannel={setChannel} onStatus={setStatus}
        onRefresh={load} loading={loading}
      />

      {!data ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : (
        <>
          <ConnectionHealth health={data.connection_health}/>
          <PublishingMatrix pub={data.publishing}/>
          <ReviewRequests rr={data.google_review_requests}/>
          <Promotions pr={data.photo_social_promotions}/>
          <Referrals rf={data.referrals}/>
          <TremendousBox t={data.tremendous} fmt$={fmt$}/>
          <div className="text-[11px] text-zinc-400 italic">{data.notes}</div>
        </>
      )}
    </div>
  );
}

function FilterBar({ from, to, channel, status, onFrom, onTo, onChannel, onStatus, onRefresh, loading }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-wrap gap-3 items-end card-crisp" data-testid="dashboard-filters">
      <label className="text-xs">From
        <input type="date" value={from} onChange={e => onFrom(e.target.value)} className="block border rounded px-2 py-1 text-sm" data-testid="filter-from"/>
      </label>
      <label className="text-xs">To
        <input type="date" value={to} onChange={e => onTo(e.target.value)} className="block border rounded px-2 py-1 text-sm" data-testid="filter-to"/>
      </label>
      <label className="text-xs">Channel
        <select value={channel} onChange={e => onChannel(e.target.value)} className="block border rounded px-2 py-1 text-sm" data-testid="filter-channel">
          <option value="">All</option>
          <option value="facebook">Facebook</option>
          <option value="instagram">Instagram</option>
          <option value="google_business_profile">Google Business Profile</option>
        </select>
      </label>
      <label className="text-xs">Status
        <select value={status} onChange={e => onStatus(e.target.value)} className="block border rounded px-2 py-1 text-sm" data-testid="filter-status">
          <option value="">All</option>
          <option value="ready_to_publish">Ready</option>
          <option value="publishing">Publishing</option>
          <option value="published">Published</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </select>
      </label>
      <Button variant="outline" onClick={onRefresh} disabled={loading} data-testid="filter-refresh"><RefreshCw className="w-4 h-4 mr-1"/> Refresh</Button>
    </div>
  );
}

function Card({ title, icon: Icon, iconColor, children, testid }) {
  return (
    <section className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid={testid}>
      <h2 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
        <Icon className={`w-5 h-5 ${iconColor}`}/> {title}
      </h2>
      {children}
    </section>
  );
}

function Pill({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${ok ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>{label}</span>
  );
}

function ConnectionHealth({ health }) {
  const rows = [
    { key: "facebook", label: "Facebook", icon: Facebook, color: "text-blue-600", info: health.facebook },
    { key: "instagram", label: "Instagram", icon: Instagram, color: "text-fuchsia-600", info: health.instagram },
    { key: "google_business_profile", label: "Google Business Profile", icon: Building2, color: "text-emerald-700", info: health.google_business_profile },
  ];
  return (
    <Card title="Connection health" icon={BarChart3} iconColor="text-zinc-700" testid="connection-health">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {rows.map(r => (
          <div key={r.key} className="border border-zinc-200 rounded-lg p-3" data-testid={`ch-${r.key}`}>
            <div className="flex items-center gap-2 mb-1"><r.icon className={`w-4 h-4 ${r.color}`}/><span className="font-medium text-sm">{r.label}</span></div>
            <div className="flex flex-wrap gap-1 mt-1">
              <Pill ok={r.info.connected} label={r.info.connected ? "Connected" : "Not connected"} />
              <Pill ok={r.info.publish_enabled} label={r.info.publish_enabled ? "Publishing on" : "Publishing off"} />
            </div>
            <div className="text-xs text-zinc-500 mt-1 truncate">{r.info.page_name || r.info.ig_username || r.info.location_title || "—"}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PublishingMatrix({ pub }) {
  const channels = ["facebook", "instagram", "google_business_profile"];
  const states = ["ready_to_publish", "publishing", "published", "failed", "skipped"];
  const labels = { facebook: "Facebook", instagram: "Instagram", google_business_profile: "GBP" };
  return (
    <Card title="Per-channel publishing" icon={BarChart3} iconColor="text-zinc-700" testid="publishing-matrix">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <MetricBox label="Awaiting approval" value={pub.awaiting_approval} testid="awaiting-approval"/>
        <MetricBox label="Ready to publish" value={pub.ready} testid="ready-to-publish"/>
        <MetricBox label="Retry required (failed)" value={pub.retry_required} highlight={pub.retry_required > 0} testid="retry-required"/>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="publishing-table">
          <thead>
            <tr className="text-xs text-zinc-500 uppercase tracking-wide border-b border-zinc-200">
              <th className="text-left py-2 pr-3">Channel</th>
              {states.map(st => <th key={st} className="text-right pr-3">{st.replace(/_/g," ")}</th>)}
            </tr>
          </thead>
          <tbody>
            {channels.map(ch => (
              <tr key={ch} className="border-b border-zinc-100" data-testid={`matrix-row-${ch}`}>
                <td className="py-2 pr-3 font-medium">{labels[ch]}</td>
                {states.map(st => (
                  <td key={st} className={`text-right pr-3 tabular-nums ${st === "failed" && pub.matrix[ch][st] > 0 ? "text-red-700 font-semibold" : ""}`}>{pub.matrix[ch][st]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function MetricBox({ label, value, sub, highlight, testid }) {
  return (
    <div className={`border rounded-lg p-3 ${highlight ? "border-red-300 bg-red-50" : "border-zinc-200"}`} data-testid={testid}>
      <div className="text-xs text-zinc-500 uppercase tracking-wide">{label}</div>
      <div className={`text-xl font-semibold ${highlight ? "text-red-700" : ""}`}>{value ?? "—"}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function ReviewRequests({ rr }) {
  return (
    <Card title="Neutral Google review requests" icon={Star} iconColor="text-amber-500" testid="review-requests-card">
      <p className="text-xs text-zinc-500 mb-3">Communications only. No discount, reward, or verification is tied to this metric.</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <MetricBox label="Total" value={rr.total} testid="rr-total"/>
        <MetricBox label="Sent" value={rr.sent} testid="rr-sent"/>
        <MetricBox label="Clicked" value={rr.clicked} sub={`CTR ${(rr.click_through_rate * 100).toFixed(1)}%`} testid="rr-clicked"/>
        <MetricBox label="Failed" value={rr.failed} highlight={rr.failed > 0} testid="rr-failed"/>
        <MetricBox label="Cancelled" value={rr.cancelled} testid="rr-cancelled"/>
      </div>
      {rr.review_growth && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="review-growth-block">
          <MetricBox label="Reviews baseline" value={rr.review_growth.baseline_count}/>
          <MetricBox label="Reviews current" value={rr.review_growth.current_count} sub={`Δ ${rr.review_growth.delta}`}/>
          <MetricBox label="Avg rating" value={rr.review_growth.current_avg_rating}/>
          <MetricBox label="Unanswered" value={rr.review_growth.unanswered_count}/>
        </div>
      )}
    </Card>
  );
}

function Promotions({ pr }) {
  const states = ["not_requested", "requested", "pending_verification", "verified", "discount_approved", "discount_applied", "expired"];
  return (
    <Card title="Photo / social-share promotions" icon={Camera} iconColor="text-fuchsia-600" testid="promotions-card">
      <p className="text-xs text-zinc-500 mb-3">Independent discount pipeline. Photo permission or social share qualifies for a discount. Separate from review requests and referral rewards.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MetricBox label="Total discount applied" value={`$${pr.total_earned_discount.toFixed(2)}`} testid="pr-total-discount"/>
        {states.map(st => (
          <MetricBox key={st} label={st.replace(/_/g," ")} value={pr.pipeline[st]} testid={`pr-${st}`}/>
        ))}
      </div>
    </Card>
  );
}

function Referrals({ rf }) {
  const rp = rf.reward_pipeline;
  return (
    <Card title="Referrals" icon={UsersRound} iconColor="text-blue-600" testid="referrals-card">
      <p className="text-xs text-zinc-500 mb-3">Paid-referral revenue and reward lifecycle. Separate from review requests and photo/social promotions.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <MetricBox label="Codes issued" value={rf.referral_codes_issued} testid="rf-codes"/>
        <MetricBox label="Referred customers" value={rf.referred_customers} testid="rf-referred"/>
        <MetricBox label="Paid referrals" value={rf.paid_referrals} testid="rf-paid"/>
        <MetricBox label="Referral revenue" value={`$${rf.paid_referral_revenue.toFixed(2)}`} testid="rf-revenue"/>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        <MetricBox label="Pending" value={rp.pending} testid="rw-pending"/>
        <MetricBox label="Verified" value={rp.verified} testid="rw-verified"/>
        <MetricBox label="Owner-approved" value={rp.owner_approved} testid="rw-approved"/>
        <MetricBox label="Processing" value={rp.processing} testid="rw-processing"/>
        <MetricBox label="Fulfilled" value={rp.fulfilled} sub={`$${rf.total_reward_paid_out.toFixed(2)}`} testid="rw-fulfilled"/>
        <MetricBox label="Failed" value={rp.failed} highlight={rp.failed > 0} testid="rw-failed"/>
      </div>
    </Card>
  );
}

function TremendousBox({ t, fmt$ }) {
  return (
    <Card title="Tremendous — spend & controls" icon={Gift} iconColor="text-amber-600" testid="tremendous-card">
      <div className="flex flex-wrap gap-1 mb-3">
        <Pill ok={t.production_enabled} label={t.production_enabled ? "Production active" : "Sandbox / manual"} />
        <Pill ok={t.pilot_completed} label={t.pilot_completed ? "Pilot complete" : "Pilot pending"} />
        <Pill ok={!t.kill_switch} label={t.kill_switch ? "KILL SWITCH ON" : "Kill switch off"} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MetricBox label="Today spend" value={fmt$(t.daily_spend_cents)} sub={`Cap ${fmt$(t.daily_cap_cents)} · Left ${fmt$(t.daily_remaining_cents)}`} testid="tr-today"/>
        <MetricBox label="Month-to-date" value={fmt$(t.monthly_spend_cents)} sub={`Cap ${fmt$(t.monthly_cap_cents)} · Left ${fmt$(t.monthly_remaining_cents)}`} testid="tr-mtd"/>
        <MetricBox label="Failed rewards" value={t.failed_reward_count} highlight={t.failed_reward_count > 0} testid="tr-failed"/>
        <MetricBox label="Fraud reviews" value={t.fraud_review_count} highlight={t.fraud_review_count > 0} testid="tr-fraud"/>
      </div>
      {t.low_balance_alert && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800 flex items-center gap-2" data-testid="tr-low-balance">
          <AlertTriangle className="w-4 h-4"/> Low balance alert: {fmt$(t.low_balance_alert.available_cents)} available (threshold {fmt$(t.low_balance_threshold_cents)}) — reported {t.low_balance_alert.at}.
        </div>
      )}
    </Card>
  );
}
